/* Paying a Mauritian wallet (Juice, my.t money) the way it is actually done at
   a counter: the guest points their phone at the merchant's QR sticker. So on
   a KIOSK branch — the only place where there IS a counter with a sticker on
   it — the payment sheet opens the camera instead of printing "you'd approve
   this in the app".

   Still a demo, like the rest of the pay-first flow: ANY QR in frame approves
   the payment. Nothing is sent to a wallet and no money moves.

   Where the camera cannot run — an old browser with no BarcodeDetector, a
   denied permission, a page not on https — the sheet turns the payment round
   the other way, which is how these wallets work anyway: it shows the guest's
   own code for the counter to scan. The sheet's Pay button stays the demo's
   manual approval either way, so the guest is never stuck behind a camera.

   Module-level on purpose: the guest menu re-renders on every status poll, and
   a component declared inside it would remount and restart the camera. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { QrCode, Check } from 'lucide-react';
import { useT } from '../../context/LanguageContext';

const GREEN = '#4E8B27';

/* The code shown to the counter is QR-SHAPED, not a real QR: the demo has no
   gateway to encode, and a decodable code would only send a curious guest into
   a wallet app that has never heard of this order. Finder squares, an alignment
   square and timing lines give it the right silhouette; the rest is filled from
   a hash of the payload, so Juice and my.t money — and two different amounts —
   draw visibly different codes rather than the same picture twice. */
const QR_MODULES = 25;

function mockQrGrid(seed) {
  // FNV-1a into a xorshift, so the same payload always draws the same code.
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const rand = () => {
    h ^= h << 13; h ^= h >>> 17; h ^= h << 5;
    return (h >>> 0) % 2 === 0;
  };

  const n = QR_MODULES;
  const grid = Array.from({ length: n }, () => Array(n).fill(false));
  // Three finder squares — top-left, top-right, bottom-left, as a real code has.
  const finder = (r0, c0) => {
    for (let r = 0; r < 7; r += 1) {
      for (let c = 0; c < 7; c += 1) {
        const ring = r === 0 || r === 6 || c === 0 || c === 6;
        const core = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        grid[r0 + r][c0 + c] = ring || core;
      }
    }
  };
  finder(0, 0); finder(0, n - 7); finder(n - 7, 0);
  // The alignment square sits where the fourth finder would be.
  for (let r = 0; r < 5; r += 1) {
    for (let c = 0; c < 5; c += 1) {
      const ring = r === 0 || r === 4 || c === 0 || c === 4;
      grid[n - 9 + r][n - 9 + c] = ring || (r === 2 && c === 2);
    }
  }
  for (let i = 8; i < n - 8; i += 1) {
    grid[6][i] = i % 2 === 0;
    grid[i][6] = i % 2 === 0;
  }
  const reserved = (r, c) =>
    (r < 8 && c < 8) || (r < 8 && c >= n - 8) || (r >= n - 8 && c < 8)
    || r === 6 || c === 6
    || (r >= n - 9 && r <= n - 5 && c >= n - 9 && c <= n - 5);
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (!reserved(r, c)) grid[r][c] = rand();
    }
  }
  return grid;
}

/**
 * @param {object}   props
 * @param {string}   props.wallet     the wallet's own name — a brand, never translated
 * @param {string}   props.amount     the total, already formatted by the page
 * @param {Function} props.onScanned  called once, with the code read, when a QR lands
 */
export default function WalletQrScanner({ wallet, amount = '', onScanned }) {
  const { t } = useT();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const firedRef = useRef(false);
  // Held in a ref so a re-render of the menu behind the sheet never restarts the camera.
  const onScannedRef = useRef(onScanned);
  onScannedRef.current = onScanned;

  const [status, setStatus] = useState('starting'); // starting | scanning | blocked | hit
  const grid = useMemo(() => mockQrGrid(`${wallet}|${amount}`), [wallet, amount]);

  useEffect(() => {
    let cancelled = false;

    const stopCamera = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
      }
    };

    (async () => {
      const Detector = typeof window !== 'undefined' ? window.BarcodeDetector : null;
      if (!Detector || !navigator.mediaDevices?.getUserMedia) {
        setStatus('blocked');
        return;
      }
      try {
        const detector = new Detector({ formats: ['qr_code'] });
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setStatus('scanning');

        const loop = async () => {
          if (cancelled || firedRef.current || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes && codes.length) {
              firedRef.current = true;
              setStatus('hit');
              stopCamera();
              // A beat on the green tick, so the guest sees the read land
              // before the sheet flips to "Processing payment…".
              setTimeout(() => onScannedRef.current?.(codes[0].rawValue || ''), 550);
              return;
            }
          } catch { /* transient decode error — keep looking */ }
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      } catch {
        // No permission, no camera, or an insecure page: fall back, don't fail.
        setStatus('blocked');
      }
    })();

    return () => { cancelled = true; stopCamera(); };
  }, []);

  return (
    <div className="mb-4">
      <style>{`
        @keyframes wqs-sweep {
          0%   { transform: translateY(6%); opacity: .25; }
          50%  { opacity: 1; }
          100% { transform: translateY(1100%); opacity: .25; }
        }
        @keyframes wqs-pop {
          0%   { transform: scale(.4); opacity: 0; }
          60%  { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .wqs-laser { animation: wqs-sweep 2.1s ease-in-out infinite; }
        .wqs-tick  { animation: wqs-pop .34s cubic-bezier(.22,1.2,.36,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .wqs-laser { animation: none; opacity: .7; }
          .wqs-tick  { animation: none; opacity: 1; transform: none; }
        }
      `}</style>

      <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        <QrCode className="h-3.5 w-3.5 shrink-0" />
        {status === 'blocked'
          ? t('guest.menu.pay.showTitle', { wallet })
          : t('guest.menu.pay.scanTitle', { wallet })}
      </p>

      {status === 'blocked' ? (
        /* No camera to scan WITH, so the payment turns round: this is the
           guest's own code, held up for the counter to scan. */
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-4 text-center">
          <svg
            viewBox={`-1 -1 ${QR_MODULES + 2} ${QR_MODULES + 2}`}
            className="mx-auto block h-auto w-40 max-w-full"
            shapeRendering="crispEdges"
            role="img"
            aria-label={t('guest.menu.pay.showCode')}
          >
            <rect
              x={-1} y={-1}
              width={QR_MODULES + 2} height={QR_MODULES + 2}
              fill="#fff"
            />
            {grid.map((row, r) =>
              row.map((on, c) => (on ? (
                <rect key={`${r}-${c}`} x={c} y={r} width={1} height={1} fill="#1F2937" />
              ) : null)),
            )}
          </svg>
          <p className="mt-3 text-xs font-medium text-gray-700">
            {t('guest.menu.pay.showCode')}
          </p>
          {amount && (
            <p className="mt-0.5 text-sm font-semibold text-gray-900">{amount}</p>
          )}
          <p className="mt-1.5 text-[11px] text-gray-400">
            {t('guest.menu.pay.showCodeDemo')}
          </p>
        </div>
      ) : (
        <>
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-black">
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              muted
              playsInline
              autoPlay
            />
            {/* Corner brackets: where to hold the counter's sticker. */}
            <div className="pointer-events-none absolute inset-6">
              {[
                'left-0 top-0 border-l-2 border-t-2 rounded-tl-lg',
                'right-0 top-0 border-r-2 border-t-2 rounded-tr-lg',
                'left-0 bottom-0 border-l-2 border-b-2 rounded-bl-lg',
                'right-0 bottom-0 border-r-2 border-b-2 rounded-br-lg',
              ].map((cls) => (
                <span
                  key={cls}
                  className={`absolute h-7 w-7 border-white/85 ${cls}`}
                />
              ))}
              {status === 'scanning' && (
                <span
                  className="wqs-laser absolute inset-x-2 top-0 block h-0.5 rounded"
                  style={{ background: 'rgba(255,255,255,.9)' }}
                />
              )}
            </div>
            {status === 'starting' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-xs text-white/80">
                {t('guest.menu.pay.scanStarting')}
              </div>
            )}
            {status === 'hit' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/65">
                <span
                  className="wqs-tick flex h-14 w-14 items-center justify-center rounded-full"
                  style={{ background: GREEN }}
                >
                  <Check className="h-7 w-7 text-white" strokeWidth={3} />
                </span>
                <span className="text-xs font-medium text-white">
                  {t('guest.menu.pay.scanned')}
                </span>
              </div>
            )}
          </div>
          <p className="mt-2 text-center text-[11px] text-gray-400">
            {t('guest.menu.pay.scanDemo')}
          </p>
        </>
      )}
    </div>
  );
}
